import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Paper, 
  Grid, 
  Typography,
  Button,
  TextField,
  IconButton,
  Tabs,
  Tab,
  Divider,
  CircularProgress,
  Slider
} from '@mui/material';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import EmojiPicker from 'emoji-picker-react';
import WhatsAppChatBubble from '../components/whatsapp/WhatsAppChatBubble';
import WhatsAppHeader from '../components/whatsapp/WhatsAppHeader';
import WhatsAppFooter from '../components/whatsapp/WhatsAppFooter';
import WhatsAppSettingsPanel from '../components/whatsapp/WhatsAppSettingsPanel';
import MicIcon from '@mui/icons-material/Mic';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import FavoriteIcon from '@mui/icons-material/Favorite';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';

const WhatsAppAnimator = () => {
  // State for WhatsApp chat configuration
  const [contactName, setContactName] = useState(() => {
    const saved = localStorage.getItem('whatsapp_contact_name');
    return saved ? JSON.parse(saved) : 'John Doe';
  });
  const [contactAvatar, setContactAvatar] = useState(() => {
    const saved = localStorage.getItem('whatsapp_contact_avatar');
    return saved ? JSON.parse(saved) : '';
  });
  const [messages, setMessages] = useState(() => {
    const saved = localStorage.getItem('whatsapp_messages');
    return saved ? JSON.parse(saved) : [];
  });
  const [senderMessage, setSenderMessage] = useState('');
  const [receiverMessage, setReceiverMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [typingUser, setTypingUser] = useState('contact');
  const [showEmojiPickerSender, setShowEmojiPickerSender] = useState(false);
  const [showEmojiPickerReceiver, setShowEmojiPickerReceiver] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [recordingProgress, setRecordingProgress] = useState(0);
  const [animationDelay, setAnimationDelay] = useState(1000);
  const [typingSpeed, setTypingSpeed] = useState(50);
  const [isAnimating, setIsAnimating] = useState(false);
  const [typingInInputBox, setTypingInInputBox] = useState('');
  const [audioDuration, setAudioDuration] = useState(10);
  const [contactAudioDuration, setContactAudioDuration] = useState(10);
  const [chatWidth, setChatWidth] = useState(400); // Default width
  const [chatHeight, setChatHeight] = useState(500); // Default height
  const [isScrolling, setIsScrolling] = useState(false); // Track if chat is currently scrolling
  const [pendingReactions, setPendingReactions] = useState([]); // Store reactions that are waiting for scroll to complete
  
  const chatContainerRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  
  // Set up scroll listener to detect when scrolling stops
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolling(true);
      
      // Clear previous timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      // Set a timeout to detect when scrolling stops
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
        
        // Process any pending reactions when scrolling stops
        if (pendingReactions.length > 0) {
          pendingReactions.forEach(reaction => {
            applyReaction(reaction.messageId, reaction.reactionType, reaction.sender, reaction.reactionObj);
          });
          setPendingReactions([]);
        }
      }, 500);
    };
    
    const currentChatContainer = chatContainerRef.current;
    
    if (currentChatContainer) {
      currentChatContainer.addEventListener('scroll', handleScroll);
    }
    
    return () => {
      if (currentChatContainer) {
        currentChatContainer.removeEventListener('scroll', handleScroll);
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [pendingReactions]);
  
  // Function to smoothly scroll to the bottom of the chat
  const smoothScrollToBottom = async () => {
    if (!chatContainerRef.current) return;
    
    // Wait a tiny bit for the DOM to update with the new message
    await new Promise(resolve => setTimeout(resolve, 50));
    
    const scrollHeight = chatContainerRef.current.scrollHeight;
    const currentScroll = chatContainerRef.current.scrollTop;
    const targetScroll = scrollHeight - chatContainerRef.current.clientHeight;
    const scrollDiff = targetScroll - currentScroll;
    
    // Always animate the scroll for a more realistic effect
    const duration = 1600; // Slower duration for more visible scrolling
    const startTime = Date.now();
    
    return new Promise(resolve => {
      const scrollStep = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function for smooth scroll
        const easeInOutCubic = t => t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
        
        chatContainerRef.current.scrollTop = currentScroll + scrollDiff * easeInOutCubic(progress);
        
        if (progress < 1) {
          requestAnimationFrame(scrollStep);
        } else {
          // Ensure we're exactly at the bottom when done
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
          resolve();
        }
      };
      
      requestAnimationFrame(scrollStep);
    });
  };
  
  // Save messages to localStorage when they change
  useEffect(() => {
    localStorage.setItem('whatsapp_messages', JSON.stringify(messages));
  }, [messages]);
  
  // Save contact info to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('whatsapp_contact_name', JSON.stringify(contactName));
  }, [contactName]);
  
  useEffect(() => {
    localStorage.setItem('whatsapp_contact_avatar', JSON.stringify(contactAvatar));
  }, [contactAvatar]);
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // Delete a specific message
  const deleteMessage = (messageId) => {
    setMessages(messages.filter(message => message.id !== messageId));
  };
  
  // Edit a specific message
  const editMessage = (messageId, newText) => {
    setMessages(messages.map(message => 
      message.id === messageId 
        ? { ...message, text: newText } 
        : message
    ));
  };
  
  // Clear all stored data
  const clearAllStoredData = () => {
    localStorage.removeItem('whatsapp_messages');
    localStorage.removeItem('whatsapp_contact_name');
    localStorage.removeItem('whatsapp_contact_avatar');
    setContactName('John Doe');
    setContactAvatar('');
    setMessages([]);
  };
  
  // Add a message from me (sender)
  const addMyMessage = () => {
    if (senderMessage.trim()) {
      setMessages([...messages, {
        id: Date.now(),
        text: senderMessage,
        sender: 'me',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }]);
      setSenderMessage('');
    }
  };
  
  // Add a message from contact (receiver)
  const addContactMessage = () => {
    if (receiverMessage.trim()) {
      setMessages([...messages, {
        id: Date.now(),
        text: receiverMessage,
        sender: 'contact',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }]);
      setReceiverMessage('');
    }
  };
  
  // Handle emoji selection for sender
  const onEmojiClickSender = (emojiObject) => {
    setSenderMessage(prev => prev + emojiObject.emoji);
    setShowEmojiPickerSender(false);
  };
  
  // Handle emoji selection for receiver
  const onEmojiClickReceiver = (emojiObject) => {
    setReceiverMessage(prev => prev + emojiObject.emoji);
    setShowEmojiPickerReceiver(false);
  };
  
  // Add reaction to a message
  const handleReaction = (messageId, reactionType, sender = 'me') => {
    // Create the reaction object
    const newReaction = {
      id: Date.now(),
      type: reactionType,
      sender: sender,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    
    // Find the message to react to
    const messageToUpdate = messages.find(message => message.id === messageId);
    
    if (!messageToUpdate) return;
    
    // Check if this reaction already exists from this sender
    const existingReactionIndex = (messageToUpdate.reactions || []).findIndex(
      r => r.type === reactionType && r.sender === sender
    );
    
    // If the chat is currently scrolling, add this reaction to pending queue
    if (isScrolling) {
      setPendingReactions(prev => [...prev, {
        messageId,
        reactionType,
        sender,
        reactionObj: newReaction,
        existingIndex: existingReactionIndex
      }]);
      return;
    }
    
    // If we're not in animation mode, apply the reaction immediately
    if (!isAnimating) {
      applyReaction(messageId, reactionType, sender, newReaction, existingReactionIndex);
    } else {
      // Only use delay during animation playback
      setTimeout(() => {
        applyReaction(messageId, reactionType, sender, newReaction, existingReactionIndex);
      }, 2000);
    }
  };
  
  // Helper function to apply a reaction to a message
  const applyReaction = (messageId, reactionType, sender, reactionObj, existingIndex = -1) => {
    setMessages(prevMessages => {
      return prevMessages.map(message => {
        if (message.id === messageId) {
          let updatedReactions = [...(message.reactions || [])];
          
          // If existingIndex wasn't provided, find it
          const reactionIndex = existingIndex >= 0 ? existingIndex : 
            updatedReactions.findIndex(r => r.type === reactionType && r.sender === sender);
          
          if (reactionIndex >= 0) {
            // Remove the reaction if it already exists (toggle behavior)
            updatedReactions.splice(reactionIndex, 1);
          } else {
            // Add the new reaction
            updatedReactions.push(reactionObj);
          }
          
          return {
            ...message,
            reactions: updatedReactions
          };
        }
        return message;
      });
    });
  };
  
  // Handle image upload for sender
  const handleSenderImageUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setMessages([...messages, {
          id: Date.now(),
          type: 'image',
          imageUrl: e.target.result,
          sender: 'me',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }]);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Handle image upload for contact
  const handleContactImageUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setMessages([...messages, {
          id: Date.now(),
          type: 'image',
          imageUrl: e.target.result,
          sender: 'contact',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }]);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Simulate typing effect for sender
  const simulateTypingSender = () => {
    setTypingUser('me');
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      addMyMessage();
    }, 2000);
  };
  
  // Simulate typing effect for receiver
  const simulateTypingReceiver = () => {
    setTypingUser('contact');
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      addContactMessage();
    }, 2000);
  };
  
  // Start animation
  const startAnimation = async () => {
    if (isAnimating) return;
    
    // Save original messages for later restoration if needed
    const originalMessages = [...messages];
    
    // Clear messages and start animation
    setMessages([]);
    setIsAnimating(true);
    
    // Start the animation sequence and auto-generate GIF when done
    await animateMessages(originalMessages);
  };
  
  // Animate messages one by one with typing effect (without capturing frames for GIF)
  const animateMessages = async (messagesToAnimate) => {
    if (!messagesToAnimate.length) {
      // If no messages, just stop the animation
      setIsAnimating(false);
      return;
    }
    
    // Start animation
    setIsAnimating(true);
    setRecordingProgress(0);
    
    // Process messages one by one
    for (let i = 0; i < messagesToAnimate.length; i++) {
      const message = messagesToAnimate[i];
      
      // Store original reactions and remove them from the message temporarily
      const originalReactions = [...(message.reactions || [])];
      const messageWithoutReactions = { ...message, reactions: [] };
      
      // Update progress
      setRecordingProgress(Math.round((i / messagesToAnimate.length) * 100));
      
      if (message.type === 'audio') {
        // For audio messages, no typing effect needed
        // Just add the message after a delay
        await new Promise(resolve => setTimeout(resolve, animationDelay));
        setMessages(prev => [...prev, messageWithoutReactions]);
        
        // Scroll to the new message with animation and wait for it to complete
        await smoothScrollToBottom();
      } else if (message.type === 'image') {
        // For image messages, add the message after a delay
        await new Promise(resolve => setTimeout(resolve, animationDelay));
        setMessages(prev => [...prev, messageWithoutReactions]);
        
        // Scroll to the new message with animation and wait for it to complete
        await smoothScrollToBottom();
      } else if (message.sender === 'me') {
        // For sender messages, show typing in the input field
        setTypingInInputBox('');
        
        // Animate typing character by character in the input box
        const text = message.text || '';
        for (let j = 0; j < text.length; j++) {
          setTypingInInputBox(text.substring(0, j + 1));
          await new Promise(resolve => setTimeout(resolve, typingSpeed));
        }
        
        // Wait a moment before sending
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Clear the input and add the message
        setTypingInInputBox('');
        setMessages(prev => [...prev, messageWithoutReactions]);
        
        // Scroll to the new message with animation and wait for it to complete
        await smoothScrollToBottom();
      } else if (message.sender === 'contact') {
        // For contact messages, show typing indicator for a duration based on message length
        setTypingUser('contact');
        setIsTyping(true);
        
        // Calculate typing duration based on message length (max 3 seconds)
        const textLength = message.text ? message.text.length : 10; // Default length for non-text messages
        const typingDuration = Math.min(3000, 500 + textLength * 30);
        await new Promise(resolve => setTimeout(resolve, typingDuration));
        
        // Add the message and clear typing indicator
        setIsTyping(false);
        setMessages(prev => [...prev, messageWithoutReactions]);
        
        // Scroll to the new message with animation and wait for it to complete
        await smoothScrollToBottom();
      } else {
        // For other messages without typing effect
        // Add the message after a delay
        await new Promise(resolve => setTimeout(resolve, animationDelay));
        setMessages(prev => [...prev, messageWithoutReactions]);
        
        // Scroll to the new message with animation and wait for it to complete
        await smoothScrollToBottom();
      }
      
      // If the message had reactions, add them after a delay
      if (originalReactions.length > 0) {
        // Wait 2 seconds before adding reactions
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Add all reactions at once instead of one by one to prevent multiple applications
        setMessages(prev => 
          prev.map(msg => 
            msg.id === message.id 
              ? { ...msg, reactions: originalReactions }
              : msg
          )
        );
      }
      
      // Wait between messages
      await new Promise(resolve => setTimeout(resolve, animationDelay / 2));
    }
    
    // Animation complete
    setIsAnimating(false);
    setRecordingProgress(100);
    
    // Show completion message
    alert('Animation complete! You can now stop your screen recording.');
  };
  
  // Handle file upload for avatar
  const handleAvatarUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setContactAvatar(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Send audio message
  const sendAudioMessage = () => {
    setMessages([...messages, {
      id: Date.now(),
      type: 'audio',
      duration: audioDuration,
      waveform: Array.from({ length: 20 }, () => Math.random()),
      sender: 'me',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }]);
  };
  
  // Send audio message as contact
  const sendContactAudioMessage = () => {
    // Generate a random waveform (array of heights from 0 to 1)
    const generateWaveform = (length = 20) =>
      Array.from({ length }, () => Math.random());
      
    setMessages([...messages, {
      id: Date.now(),
      type: 'audio',
      duration: contactAudioDuration,
      waveform: generateWaveform(),
      sender: 'contact',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }]);
  };
  
  return (
    <Container maxWidth="lg">
      <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          WhatsApp Conversation Animator
        </Typography>
        <Typography variant="body1" paragraph>
          Create realistic WhatsApp conversation animations with typing effects, emojis, and save as GIF.
        </Typography>
        
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="Chat View" />
          <Tab label="Settings" />
        </Tabs>
        
        <Divider sx={{ mb: 2 }} />
        
        {activeTab === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 3, mb: 2 }}>
                <Typography variant="h6" gutterBottom>Controls</Typography>
                
                <Grid container spacing={2}>
                  {/* Receiver (Contact) Input */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>Contact Message</Typography>
                    <TextField
                      fullWidth
                      label="Contact Message"
                      variant="outlined"
                      value={receiverMessage}
                      onChange={(e) => setReceiverMessage(e.target.value)}
                      multiline
                      rows={3}
                      placeholder="Use *bold*, _italic_, ~strikethrough~, and line breaks"
                      sx={{ mb: 1 }}
                    />
                    
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button 
                        variant="contained" 
                        color="secondary"
                        onClick={simulateTypingReceiver}
                        disabled={!receiverMessage.trim() || isTyping}
                        fullWidth
                      >
                        Add as Contact
                      </Button>
                      <IconButton onClick={() => setShowEmojiPickerReceiver(!showEmojiPickerReceiver)}>
                        <EmojiEmotionsIcon />
                      </IconButton>
                    </Box>
                    
                    {showEmojiPickerReceiver && (
                      <Box sx={{ mt: 1, position: 'relative', zIndex: 10 }}>
                        <EmojiPicker onEmojiClick={onEmojiClickReceiver} />
                      </Box>
                    )}
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Typography variant="subtitle2" gutterBottom>Send Audio as Contact</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ mr: 1, minWidth: '60px' }}>
                        Duration: {contactAudioDuration}s
                      </Typography>
                      <Slider
                        value={contactAudioDuration}
                        min={1}
                        max={30}
                        step={1}
                        onChange={(_, val) => setContactAudioDuration(val)}
                        sx={{ flex: 1 }}
                      />
                    </Box>
                    
                    <Button 
                      variant="contained" 
                      color="secondary"
                      onClick={sendContactAudioMessage}
                      fullWidth
                      startIcon={<MicIcon />}
                    >
                      Send Audio
                    </Button>
                    
                    <Button 
                      variant="contained" 
                      color="secondary"
                      component="label"
                      fullWidth
                      sx={{ mt: 1 }}
                    >
                      Send Image
                      <input 
                        type="file" 
                        accept="image/*" 
                        hidden 
                        onChange={handleContactImageUpload}
                      />
                    </Button>
                  </Grid>
                  
                  {/* Sender (Me) Input */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>Your Message</Typography>
                    <TextField
                      fullWidth
                      label="Your Message"
                      variant="outlined"
                      value={senderMessage}
                      onChange={(e) => setSenderMessage(e.target.value)}
                      multiline
                      rows={3}
                      placeholder="Use *bold*, _italic_, ~strikethrough~, and line breaks"
                      sx={{ mb: 1 }}
                    />
                    
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button 
                        variant="contained" 
                        color="primary"
                        onClick={simulateTypingSender}
                        disabled={!senderMessage.trim() || isTyping}
                        fullWidth
                      >
                        Add as Me
                      </Button>
                      <IconButton onClick={() => setShowEmojiPickerSender(!showEmojiPickerSender)}>
                        <EmojiEmotionsIcon />
                      </IconButton>
                    </Box>
                    
                    {showEmojiPickerSender && (
                      <Box sx={{ mt: 1, position: 'relative', zIndex: 10 }}>
                        <EmojiPicker onEmojiClick={onEmojiClickSender} />
                      </Box>
                    )}
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Typography variant="subtitle2" gutterBottom>Send Audio Message</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ mr: 1, minWidth: '60px' }}>
                        Duration: {audioDuration}s
                      </Typography>
                      <Slider
                        value={audioDuration}
                        min={1}
                        max={30}
                        step={1}
                        onChange={(_, val) => setAudioDuration(val)}
                        sx={{ flex: 1 }}
                      />
                    </Box>
                    
                    <Button 
                      variant="contained" 
                      color="success"
                      onClick={sendAudioMessage}
                      fullWidth
                      startIcon={<MicIcon />}
                    >
                      Send Audio
                    </Button>
                    
                    <Button 
                      variant="contained" 
                      color="success"
                      component="label"
                      fullWidth
                      sx={{ mt: 1 }}
                    >
                      Send Image
                      <input 
                        type="file" 
                        accept="image/*" 
                        hidden 
                        onChange={handleSenderImageUpload}
                      />
                    </Button>
                  </Grid>
                </Grid>
                
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle1" gutterBottom>Message Reactions</Typography>
                
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12}>
                    <Typography variant="body2" gutterBottom>Select a reaction:</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                      <Button
                        variant="outlined"
                        startIcon={<ThumbUpAltIcon />}
                        onClick={() => handleReaction(messages[messages.length - 1].id, 'like')}
                        size="small"
                      >
                        Like
                      </Button>
                      <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<FavoriteIcon />}
                        onClick={() => handleReaction(messages[messages.length - 1].id, 'love')}
                        size="small"
                      >
                        Love
                      </Button>
                      <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<SentimentVerySatisfiedIcon />}
                        onClick={() => handleReaction(messages[messages.length - 1].id, 'laugh')}
                        size="small"
                      >
                        Laugh
                      </Button>
                      <Button
                        variant="outlined"
                        color="info"
                        startIcon={<SentimentVeryDissatisfiedIcon />}
                        onClick={() => handleReaction(messages[messages.length - 1].id, 'sad')}
                        size="small"
                      >
                        Sad
                      </Button>
                      <Button
                        variant="outlined"
                        color="success"
                        startIcon={<EmojiEmotionsIcon />}
                        onClick={() => handleReaction(messages[messages.length - 1].id, 'wow')}
                        size="small"
                      >
                        Wow
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
                
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle1" gutterBottom>Animation Controls</Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" gutterBottom>Animation Delay (ms)</Typography>
                    <TextField
                      type="number"
                      size="small"
                      value={animationDelay}
                      onChange={(e) => setAnimationDelay(parseInt(e.target.value) || 1000)}
                      InputProps={{
                        endAdornment: <Typography variant="caption">ms</Typography>,
                        inputProps: { min: 0, max: 10000 }
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" gutterBottom>Typing Speed (ms)</Typography>
                    <TextField
                      type="number"
                      size="small"
                      value={typingSpeed}
                      onChange={(e) => setTypingSpeed(parseInt(e.target.value) || 50)}
                      InputProps={{
                        endAdornment: <Typography variant="caption">ms</Typography>,
                        inputProps: { min: 10, max: 500 }
                      }}
                      fullWidth
                    />
                  </Grid>
                </Grid>
                
                <Typography variant="subtitle1" gutterBottom>Chat Size</Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" gutterBottom>Chat Width (px)</Typography>
                    <TextField
                      size="small"
                      value={chatWidth}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow empty field for typing
                        if (value === '') {
                          setChatWidth('');
                        } else {
                          const numValue = parseInt(value);
                          if (!isNaN(numValue)) {
                            setChatWidth(numValue);
                          }
                        }
                      }}
                      onBlur={() => {
                        // When field loses focus, ensure value is within range
                        if (chatWidth === '' || chatWidth < 200) {
                          setChatWidth(200);
                        } else if (chatWidth > 800) {
                          setChatWidth(800);
                        }
                      }}
                      InputProps={{
                        endAdornment: <Typography variant="caption">px</Typography>,
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" gutterBottom>Chat Height (px)</Typography>
                    <TextField
                      size="small"
                      value={chatHeight}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow empty field for typing
                        if (value === '') {
                          setChatHeight('');
                        } else {
                          const numValue = parseInt(value);
                          if (!isNaN(numValue)) {
                            setChatHeight(numValue);
                          }
                        }
                      }}
                      onBlur={() => {
                        // When field loses focus, ensure value is within range
                        if (chatHeight === '' || chatHeight < 300) {
                          setChatHeight(300);
                        } else if (chatHeight > 1000) {
                          setChatHeight(1000);
                        }
                      }}
                      InputProps={{
                        endAdornment: <Typography variant="caption">px</Typography>,
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button 
                      variant="outlined" 
                      color="primary" 
                      onClick={() => {
                        setChatWidth(400);
                        setChatHeight(500);
                      }}
                      fullWidth
                      size="small"
                    >
                      Reset to Default Size
                    </Button>
                  </Grid>
                </Grid>
                
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  {/* Removed Play Animation button */}
                </Box>
                

                
                {isAnimating && (
                  <Box sx={{ width: '100%', mt: 1, display: 'flex', alignItems: 'center' }}>
                    <CircularProgress 
                      variant="determinate" 
                      value={recordingProgress} 
                      size={24} 
                      sx={{ mr: 1 }} 
                    />
                    <Typography variant="body2" component="span">
                      {Math.round(recordingProgress)}% - {recordingProgress >= 100 ? 'Animation complete!' : 'Playing animation...'}
                    </Typography>
                  </Box>
                )}
                

              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={3} 
                className="whatsapp-screen-container"
                id="whatsapp-screen"
                sx={{ 
                  width: chatWidth, 
                  maxWidth: chatWidth, 
                  mx: 'auto',
                  overflow: 'hidden',
                  borderRadius: 2,
                  position: 'relative', // Ensure proper positioning
                  mb: 2 // Add margin bottom for the animation controls
                }}
              >
                <WhatsAppHeader 
                  contactName={contactName}
                  contactAvatar={contactAvatar}
                />
                
                <Box 
                  ref={chatContainerRef}
                  sx={{ 
                    height: chatHeight, 
                    overflowY: 'auto',
                    p: 1,
                    backgroundColor: '#ECE5DD',
                    backgroundImage: `url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')`,
                    backgroundSize: 'cover',
                    backgroundRepeat: 'repeat',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  {messages.map(message => (
                    <WhatsAppChatBubble 
                      key={message.id}
                      message={message}
                      contactName={contactName}
                      onDelete={deleteMessage}
                      onEdit={editMessage}
                      onReaction={(reactionType, sender) => handleReaction(message.id, reactionType, sender)}
                    />
                  ))}
                </Box>
                
                <WhatsAppFooter 
                  onSendAudio={audioMsg => setMessages([...messages, {...audioMsg, id: Date.now()}])} 
                  typingText={typingInInputBox} 
                  isTyping={isTyping} 
                  typingUser={typingUser} 
                />
              </Paper>
              
              {/* Animation controls moved below the WhatsApp screen */}
              <Paper elevation={3} sx={{ p: 2, width: chatWidth, mx: 'auto' }}>
                <Typography variant="subtitle1" gutterBottom>Animation Controls</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  {!isAnimating ? (
                    <Button 
                      variant="contained" 
                      color="primary"
                      startIcon={<PlayArrowIcon />}
                      onClick={startAnimation}
                      disabled={messages.length === 0}
                      fullWidth
                    >
                      Play Animation
                    </Button>
                  ) : (
                    <Button 
                      variant="contained" 
                      color="primary"
                      disabled
                      fullWidth
                    >
                      Playing Animation...
                    </Button>
                  )}
                </Box>
                
                {isAnimating && (
                  <Box sx={{ width: '100%', mt: 1, display: 'flex', alignItems: 'center' }}>
                    <CircularProgress 
                      variant="determinate" 
                      value={recordingProgress} 
                      size={24} 
                      sx={{ mr: 1 }} 
                    />
                    <Typography variant="body2" component="span">
                      {Math.round(recordingProgress)}% - {recordingProgress >= 100 ? 'Animation complete!' : 'Playing animation...'}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}
        
        {activeTab === 1 && (
          <WhatsAppSettingsPanel 
            contactName={contactName}
            setContactName={setContactName}
            contactAvatar={contactAvatar}
            setContactAvatar={setContactAvatar}
            handleAvatarUpload={handleAvatarUpload}
            messages={messages}
            setMessages={setMessages}
            clearAllStoredData={clearAllStoredData}
          />
        )}
      </Paper>
    </Container>
  );
};

export default WhatsAppAnimator;
